import React, { useMemo, useRef } from 'react';
import { Card, LoadingSpinner, Pagination } from 'components/common';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { FiList, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const STATUS_COLORS = {
  new_pcv:     { bg: '#F1F5F9', text: '#475569' },
  quotation:   { bg: '#DBEAFE', text: '#1D4ED8' },
  tendering:   { bg: '#E0F2FE', text: '#0369A1' },
  negotiation: { bg: '#FEF3C7', text: '#92400E' },
  won:         { bg: '#D1FAE5', text: '#065F46' },
  lost:        { bg: '#FEE2E2', text: '#B91C1C' },
};

const statusStyle = (statusName = '') => {
  const lower = (statusName || '').toLowerCase();
  if (lower.includes('lost') || lower.includes('cancel'))                    return STATUS_COLORS.lost;
  if (lower === 'won' || lower.includes('sign') || lower.includes('complet')) return STATUS_COLORS.won;
  if (lower.includes('negot'))                                               return STATUS_COLORS.negotiation;
  if (lower.includes('tender'))                                              return STATUS_COLORS.tendering;
  if (lower.includes('quot'))                                                return STATUS_COLORS.quotation;
  return STATUS_COLORS.new_pcv;
};

const SalesDetailedTable = ({
  data,
  pagination,
  loading,
  error,
  onPageChange,
  onPageSizeChange,
  onExportAll,
  exportLoading
}) => {
  const gridRef = useRef(null);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Q / J Number',
      field:       'q_number',
      width:       140,
      pinned:      'left',
      valueGetter:  p => p.data?.j_number || p.data?.q_number || '—',
      cellRenderer: (p) => (
        <Link
          to={`/crm/projects/${p.data?.id}`}
          target="_blank"
          rel="noreferrer"
          className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline"
        >
          {p.value}
        </Link>
      ),
      cellStyle:   { fontWeight: 600, color: '#1D4ED8' }
    },
    {
      headerName: 'Project Name',
      field:       'name',
      flex:        2,
      minWidth:    160,
      cellRenderer: (p) => (
        <span className="tw-flex tw-items-center tw-gap-1.5">
          {p.data?.from_lead && (
            <span className="tw-text-xs tw-bg-violet-100 tw-text-violet-700 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-font-semibold">Lead</span>
          )}
          <span><Link to={`/crm/projects/${p.data?.id}`} target="_blank" rel="noreferrer">{p.value}</Link></span>
        </span>
      )
    },
    { headerName: 'Customer',      field: 'company_name',    flex: 1, minWidth: 120 },
    { headerName: 'Sales Incharge', field: 'sales_incharge', width: 130 },
    { headerName: 'Sales Unit',    field: 'sales_unit_name', width: 110 },
    {
      headerName: 'Status',
      field:       'sales_pipeline_name',
      width:       160,
      cellRenderer: (p) => {
        const cfg = statusStyle(p.value);
        return p.value ? (
          <span
            className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-medium"
            style={{ backgroundColor: cfg.bg, color: cfg.text }}
          >
            {p.value}
          </span>
        ) : '—';
      }
    },
    {
      headerName: 'Pipeline Value',
      field:       'amount',
      width:       130,
      type:        'numericColumn',
      valueFormatter: p => p.value ? fmtM(p.value) : '—',
      cellStyle:   { textAlign: 'right', fontWeight: 600 }
    },
    {
      headerName: 'Weight (MT)',
      field:       'weight',
      width:       110,
      type:        'numericColumn',
      valueFormatter: p => p.value ? Number(p.value).toFixed(2) : '—',
      cellStyle:   { textAlign: 'right' }
    },
    { headerName: 'Country',  field: 'pj_country',  width: 90,  cellStyle: { color: '#6B7280', fontSize: '12px' } },
    { headerName: 'Location', field: 'pj_location', flex: 1, minWidth: 100, cellStyle: { color: '#6B7280', fontSize: '12px' } },
    { headerName: 'Open date',  field: 'open_date',  width: 100, cellStyle: { color: '#6B7280', fontSize: '12px' } }
  ], []);

  return (
    <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
        <FiList className="tw-text-gray-500" size={18} />
        <h3 className="tw-font-bold tw-text-gray-900">Detailed Records</h3>
        <span className="tw-text-xs tw-text-gray-400">
          {pagination?.total_records?.toLocaleString() || 0} records
        </span>

        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
          {/* Export all records */}
          <button
            onClick={onExportAll}
            disabled={exportLoading || loading}
            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-border tw-border-blue-300 tw-rounded tw-bg-blue-50 tw-text-blue-700 hover:tw-bg-blue-100 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors tw-cursor-pointer"
            title="Export all records as CSV"
          >
            {exportLoading ? (
              <span className="tw-w-3 tw-h-3 tw-border-2 tw-border-blue-400 tw-border-t-transparent tw-rounded-xl tw-animate-spin tw-inline-block" />
            ) : (
              <FiDownload size={13} />
            )}
            Export All
          </button>
        </div>
      </div>

      {error && (
        <div className="tw-px-5 tw-py-3 tw-bg-red-50 tw-border-b tw-border-red-100">
          <p className="tw-text-red-600 tw-text-sm">Error loading detailed records</p>
        </div>
      )}

      <div className="tw-relative">
        {loading && <LoadingSpinner overlay message="Loading records…" />}
        <div className="ag-theme-quartz" style={{ height: 420, '--ag-font-size': '12px', '--ag-row-height': '36px', '--ag-header-height': '36px' }}>
          <AgGridReact
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            suppressRowClickSelection
            animateRows
            overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:12px">No records found</span>'
          />
        </div>
      </div>

      {pagination && (
        <div className="tw-px-5 tw-py-2.5 tw-border-t tw-border-gray-100 tw-bg-gray-50">
          <Pagination
            currentPage={pagination.current_page || 1}
            totalPages={pagination.total_pages || 1}
            totalResults={pagination.total_records || 0}
            perPage={pagination.per_page || 50}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[20, 50, 100, 200]}
            disabled={loading}
          />
        </div>
      )}
    </Card>
  );
};

SalesDetailedTable.propTypes = {
  data:             PropTypes.array,
  pagination:       PropTypes.object,
  loading:          PropTypes.bool,
  error:            PropTypes.any,
  onPageChange:     PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onExportAll:      PropTypes.func,
  exportLoading:    PropTypes.bool
};
SalesDetailedTable.defaultProps = {
  data:          [],
  pagination:    null,
  loading:       false,
  error:         null,
  onExportAll:   null,
  exportLoading: false
};

export default SalesDetailedTable;
